/**
 * SEO configuration for Healix Medical website
 */
// 10-Year Anniversary Logo URL - High Quality
// Using Cloudinary optimization: q_100 (maximum quality), f_auto (auto format), dpr_auto (retina support)
const LOGO_URL = "https://res.cloudinary.com/dhbe6wtod/image/upload/q_100,f_auto,dpr_auto/v1771250399/healix/new%20logos/1OTH-blue_t8itk4_5ad7ba.png";

export const SEO_CONFIG = {
  defaultTitle: "Healix Medical - Innovative Diagnostic Solutions",
  defaultDescription: "Leading provider of innovative diagnostic solutions, medical equipment, and laboratory services in Malawi. Authorized distributor of internationally recognized diagnostic brands.",
  siteUrl: process.env.REACT_APP_SITE_URL || "https://www.healix.tech",
  siteName: "Healix Medical",
  defaultImage: LOGO_URL,
  twitterHandle: "@healixmedical",
  keywords: [
    "medical equipment",
    "diagnostic solutions",
    "laboratory equipment",
    "hematology",
    "microbiology",
    "chemistry",
    "flow cytometry",
    "Malawi medical supplies",
    "Healix Medical",
    "diagnostic analyzers",
    "clinical diagnostics"
  ],
};

/**
 * Page-specific SEO metadata
 */
export const PAGE_SEO = {
  home: {
    title: "Healix Medical - Innovative Diagnostic Solutions",
    description: "Leading provider of innovative diagnostic solutions, medical equipment, and laboratory services in Malawi.",
    keywords: "medical equipment, diagnostic solutions, laboratory equipment, Malawi",
  },
  products: {
    title: "Products - Healix Medical",
    description: "Browse our range of diagnostic equipment including hematology analyzers, microbiology systems, chemistry analyzers, and flow cytometry instruments.",
    keywords: "diagnostic equipment, hematology analyzers, microbiology systems, chemistry analyzers",
  },
  services: {
    title: "Services - Healix Medical",
    description: "Comprehensive diagnostic services including technical support, training, maintenance, and 24/7 support for laboratory equipment.",
    keywords: "diagnostic services, technical support, laboratory training, equipment maintenance",
  },
  about: {
    title: "About Us - Healix Medical",
    description: "Learn about Healix Medical, our team, and our commitment to providing the best diagnostic solutions in Malawi.",
    keywords: "about Healix Medical, diagnostic solutions provider, Malawi medical company",
  },
  contact: {
    title: "Contact Us - Healix Medical",
    description: "Get in touch with Healix Medical for inquiries about our products and services. We're here to help with your diagnostic equipment needs.",
    keywords: "contact Healix Medical, medical equipment inquiry, diagnostic solutions contact",
  },
  blogs: {
    title: "Blog & Updates - Healix Medical",
    description: "News, insights, and updates from Healix Medical. Follow our LinkedIn for the latest on diagnostic solutions and medical equipment in Malawi.",
    keywords: "Healix Medical blog, diagnostic solutions news, medical equipment updates, Malawi healthcare",
  },
  testimonials: {
    title: "Testimonials - Healix Medical",
    description: "Read what our customers say about Healix Medical's diagnostic solutions, medical equipment, and exceptional service in Malawi.",
    keywords: "Healix Medical testimonials, customer reviews, medical equipment feedback, diagnostic solutions reviews",
  },
};

/**
 * Generate structured data (JSON-LD) for organization
 */
export const getOrganizationStructuredData = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Healix Medical",
    "url": SEO_CONFIG.siteUrl,
    "logo": LOGO_URL,
    "description": "Leading provider of innovative diagnostic solutions, medical equipment, and laboratory services in Malawi",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Blantyre",
      "addressCountry": "Malawi",
      "postalCode": "3105",
      "postOfficeBoxNumber": "P. O. Box 3105"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+265-888-729-116",
      "contactType": "Customer Service",
      "email": "support@healixmw.tech"
    },
    "sameAs": [
      "https://www.linkedin.com/company/healix-medical",
      "https://www.facebook.com/healixmedical"
    ]
  };
};

/**
 * Generate structured data (JSON-LD) for a product
 * @param {Object} product - Product object with name, description, image, category
 * @param {string} url - Full URL to the product page
 * @returns {Object} - Structured data object
 */
export const getProductStructuredData = (product, url) => {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.fullDescription || product.summary,
    "image": product.image,
    "category": product.category,
    "brand": {
      "@type": "Brand",
      "name": "Healix Medical"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "MWK",
      "seller": {
        "@type": "Organization",
        "name": "Healix Medical"
      }
    },
    "url": url
  };
};

/**
 * Generate structured data (JSON-LD) for a service
 * @param {Object} service - Service object with serviceName, serviceDescription
 * @param {string} url - Full URL to the service page
 * @returns {Object} - Structured data object
 */
export const getServiceStructuredData = (service, url) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.serviceName,
    "description": service.serviceDescription,
    "provider": {
      "@type": "Organization",
      "name": "Healix Medical",
      "url": SEO_CONFIG.siteUrl
    },
    "areaServed": {
      "@type": "Country",
      "name": "Malawi"
    },
    "url": url
  };
};

/**
 * Generate structured data (JSON-LD) for breadcrumbs
 * @param {Array} items - Array of {name, url} objects
 * @returns {Object} - Structured data object
 */
export const getBreadcrumbStructuredData = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};

/**
 * Generate structured data (JSON-LD) for a blog article
 * @param {Object} article - Article object with title, description, image, publishedDate
 * @param {string} url - Full URL to the article
 * @returns {Object} - Structured data object
 */
export const getArticleStructuredData = (article, url) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description || article.excerpt,
    "image": article.image,
    "datePublished": article.publishedDate || article.date,
    "author": {
      "@type": "Organization",
      "name": "Healix Medical"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Healix Medical",
      "logo": {
        "@type": "ImageObject",
        "url": LOGO_URL
      }
    },
    "url": url
  };
};
